//this file responsible for  required methods for all tasks. Although contains information, when our application is start/stop

import Phaser from 'phaser';
import { RandomNumberGenerator } from '../scene/RandomNumberGenerator';
//#region Task Interfaces

// The tagging interface that designates something as a task configuration.
export interface ITaskConfiguration {
  seed: number;

  enabled: boolean;
  // A seeded random generator that needs to be used by a task

  Copy(): ITaskConfiguration;

  /**
   * This should only copy over the values from the difficulty config which are relevant for difficulty for this task.
   */
  ApplyDifficulty(difficultyConfig: ITaskConfiguration): void;
}

export abstract class ATaskConfiguration implements ITaskConfiguration {
  protected _seed: number;
  enabled: boolean = false;

  protected constructor(seed: number) {
    this._seed = seed;
  }

  public get seed() {
    return this._seed;
  }

  public set seed(seed: number) {
    this._seed = seed;
  }

  abstract Copy(): ITaskConfiguration;

  /**
   * This should only copy over the values from the difficulty config which are relevant for difficulty for this task.
   */
  abstract ApplyDifficulty(difficultyConfig: ITaskConfiguration): void;
}

export interface ITask<Configuration extends ITaskConfiguration> {
  name: string;

  // Configuration that was used to create or update the task.
  configuration: Configuration;

  // Whether the task is currently running/active
  isActive: boolean;

  Accepts(configuration: ITaskConfiguration): boolean;

  Create(configuration: Configuration, scene: Phaser.Scene): void;

  // Starts or resumes the task and ensures it's visible
  Start(scene: Phaser.Scene): void;

  // Pauses / hides the task
  Stop(scene: Phaser.Scene): void;

  Update(configuration: Configuration, scene: Phaser.Scene, firstCreateCall: boolean): void;

  // Called every frame
  Render(): void;

  HandleUserResponseFromServer(isCorrect: boolean): void;
}

export abstract class ATask<Configuration extends ITaskConfiguration> implements ITask<Configuration> {
  random: RandomNumberGenerator;
  seed: number;
  isCorrect: boolean = false;

  // Whether the task is currently running/active
  isActive: boolean = false;

  name: string;

  onServerResponseSignal: boolean | undefined;

  constructor(config: Configuration, scene: Phaser.Scene, name: string) {
    this._configuration = config;
    this.name = name;
    this.seed = config.seed;
    this.random = new RandomNumberGenerator(this.seed);
    this.RandomUpdated();
  }

  // Configuration that was used to create or update the task.
  protected _configuration: Configuration;

  get configuration(): Configuration {
    return this._configuration.Copy() as Configuration;
  }

  set configuration(value: Configuration) {
    this._configuration = value;
  }

  Accepts(configuration: ITaskConfiguration): configuration is Configuration {
    return configuration instanceof this.configuration.constructor;
  }

  // Only called once, used to create the task's initial setup
  abstract Create(configuration: Configuration, scene: Phaser.Scene): void;

  // Starts or resumes the task and ensures it is visible
  abstract Start(scene: Phaser.Scene): void;

  // Pauses / hides the task
  abstract Stop(scene: Phaser.Scene): void;

  Render(): void {}

  Update(configuration: Configuration, scene: Phaser.Scene, firstCreateCall: boolean): void {
    // console.log(`[Task::Update] ${this.name}\n`, configuration);

    this.seed = configuration.seed;

    this.random = new RandomNumberGenerator(this.seed);
    this.RandomUpdated();
    let newConfiguration = configuration.Copy() as Configuration;
    let oldConfiguration = this._configuration;
    this.OnUpdate(oldConfiguration, newConfiguration, scene, firstCreateCall);
    this.configuration = newConfiguration;
  }

  /**
   * Will be called when we create a new RandomDataGenerator (this.random) or update the seed.
   */
  RandomUpdated(): void {}

  HandleUserResponseFromServer(isCorrect: boolean) {
    this.onServerResponseSignal = isCorrect;
  }

  protected abstract OnUpdate(
    oldConfiguration: Configuration,
    newConfiguration: Configuration,
    scene: Phaser.Scene,
    firstCreateCall: boolean
  ): void;
}
